import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  CheckCircle,
  Building,
  ArrowRight,
  Sparkles,
  Shield,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Toast, useToast } from "../HROnboardingPanel";

const LoginPage = ({ setAuthMode, resetForm }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error: authError, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Display auth errors if they occur
  useEffect(() => {
    if (authError) {
      setErrors((prev) => ({
        ...prev,
        form: authError,
      }));
    }
  }, [authError]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    console.log("Form submitted", e);
    e.preventDefault();

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);

    try {
      const { email, password } = formData;
      if (email === "" || password === "") {
        setErrors({
          form: "Email and password are required",
        });
        return;
      }
      const result = await login(email, password);

      if (result.success) {
        setShowSuccessMessage(true);
        // Redirect to the intended page or home
        const from = location.state?.from?.pathname || "/";
        navigate(from, { replace: true });
      } else {
        setErrors((prev) => ({
          ...prev,
          form: result.error || "Login failed. Please try again.",
        }));
        setLoginAttempts((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({
        form: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleSubmit(e);
        console.log("Enter key pressed", e);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [formData]);

  const { toasts, showToast, removeToast } = useToast();

  // Handle forgot password
  const handleForgotPassword = async () => {
    console.log("Forgot password clicked");
    // In a real app, you would navigate to forgot password page
    try {
      const { email } = formData;
      if (email === "") {
        showToast("Email are required", "error");
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        // Redirect to the intended page or home
        showToast(data.message, data.status);
      } else {
        showToast(data.message || "Login failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Login error:", error);
      showToast("An unexpected error occurred. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
    
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                onClose={() => removeToast(toast.id)}
              />
            ))}
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute inset-0 top-2 left-2 right-2 flex justify-between">
            <img
              src={`${process.env.PUBLIC_URL}/images/kajaria-logo.png`}
              alt="Logo 1"
              className="filter h-6 md:h-10 sm:h-5"
            />
            <div className="flex gap-8">
              <img
                src={`${process.env.PUBLIC_URL}/images/kerovit_logo.png`}
                alt="Kerovit"
                className="filter h-8 md:h-10 sm:h-5"
              />
              <img
                src={`${process.env.PUBLIC_URL}/images/gresbond-logo.png`}
                alt="Gresbond"
                className="filter h-8 md:h-10 sm:h-5"
              />
            </div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Company Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-orange-200 mb-2 tracking-tight">
            Document Management
          </h1>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
            Welcome
          </h1>
          <p className="text-gray-300 text-lg">Sign in to access</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-400/30 rounded-2xl backdrop-blur-sm animate-fadeIn">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div>
                  <p className="text-green-300 font-medium">
                    Login Successful!
                  </p>
                  <p className="text-green-400/80 text-sm">
                    Redirecting to dashboard...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* General Error Message */}
          {errors.general && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-2xl backdrop-blur-sm animate-shake">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="text-red-300 text-sm">{errors.general}</p>
              </div>
            </div>
          )}

          {/* Form Error Message */}
          {errors.form && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-2xl backdrop-blur-sm animate-shake">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="text-red-300 text-sm">{errors.form}</p>
              </div>
            </div>
          )}

          {/* Login Attempts Warning */}
          {loginAttempts >= 2 && (
            <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-400/30 rounded-2xl backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-yellow-400" />
                <div>
                  <p className="text-yellow-300 font-medium">
                    Multiple failed attempts
                  </p>
                  <p className="text-yellow-400/80 text-sm">
                    Please double-check your credentials or contact support
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Email Field */}
            <div className="group">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-200 mb-2"
              >
                Email Address/Employee ID/Reference ID
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
                    focusedField === "email" ? "text-blue-400" : "text-gray-400"
                  }`}
                >
                  <Mail className="h-5 w-5" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  className={`block w-full pl-12 pr-4 py-4 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                    errors.email
                      ? "border-red-400/50 bg-red-500/10 shadow-lg shadow-red-500/20"
                      : focusedField === "email"
                      ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                      : "border-white/20 hover:border-white/30"
                  }`}
                  placeholder="Enter your email address/employee id"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-2 animate-slideDown">
                  <AlertCircle className="h-4 w-4" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="group">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-200 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
                    focusedField === "password"
                      ? "text-blue-400"
                      : "text-gray-400"
                  }`}
                >
                  <Lock className="h-5 w-5" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  className={`block w-full pl-12 pr-12 py-4 bg-white/10 border rounded-2xl backdrop-blur-sm placeholder-gray-400 text-white focus:outline-none transition-all duration-300 ${
                    errors.password
                      ? "border-red-400/50 bg-red-500/10 shadow-lg shadow-red-500/20"
                      : focusedField === "password"
                      ? "border-blue-400/50 bg-blue-500/10 shadow-lg shadow-blue-500/20 transform scale-[1.02]"
                      : "border-white/20 hover:border-white/30"
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-2 animate-slideDown">
                  <AlertCircle className="h-4 w-4" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center group cursor-pointer">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`relative w-5 h-5 rounded-md border-2 transition-all duration-200 ${
                    rememberMe
                      ? "bg-blue-500 border-blue-500"
                      : "border-gray-400 group-hover:border-gray-300"
                  }`}
                >
                  {rememberMe && (
                    <CheckCircle className="w-3 h-3 text-white absolute top-0.5 left-0.5 transform scale-75" />
                  )}
                </div>
                <span className="ml-3 text-sm text-gray-300 group-hover:text-white transition-colors duration-200">
                  Remember me
                </span>
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200 hover:underline"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || showSuccessMessage}
              onClick={handleSubmit}
              className="w-full group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="relative flex justify-center items-center gap-3">
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                    <span>Signing in...</span>
                  </>
                ) : showSuccessMessage ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Success!</span>
                  </>
                ) : (
                  <>
                    <span>Sign in</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Demo Credentials & New Account Link */}
          <div className="mt-8 pt-6 border-t border-white/20">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="text-center">
                <button
                  onClick={() => {
                    setAuthMode && setAuthMode("verify-eid");
                    resetForm && resetForm();
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors duration-200 hover:underline"
                >
                  New to the Portal? Create account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image: linear-gradient(
              rgba(255, 255, 255, 0.1) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.1) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
